import React, { memo, useMemo, useRef, useState } from 'react';

import { Center, Text } from '@chakra-ui/react';

import { QueryFunctionContext, useQuery } from 'react-query';

import { washHub } from 'store/slices';
import { useWashHubListener } from 'hooks';
import { useSKU } from 'hooks/useSKU';
import { fetchOrders } from 'api/order';
import { Order } from 'types/order';
import { Status } from 'types/items';
import { DataTable, ServerSort } from 'components/modules/DataTable';
import {
  EditOrderModal,
  EditOrderModalRef,
} from 'components/modules/EditOrderModal';

import { OrderNavButton } from './OrderNavButton';
import { buildOrderColumns, SkuItemsById } from './ordersColumns';

export type OrdersTableProps = {
  /** Element used in the react-query key for this tab ('all' or a Status). */
  statusKey: string | Status;
  status?: Status;
  excludeStatus?: Status[];
  startDate?: Date;
  endDate?: Date;
  venue?: string[];
  accountManagers?: string[];
  invoiced: boolean | undefined;
  feeAdjustmentTypes?: string[];
  /** Debounced search term; matches order number, site, or SKU name. */
  search?: string;
};

/**
 * Shared body of the four Orders tabs — the query, sort state, table, and
 * EditOrderModal wiring were copy-pasted per tab and differed only in the
 * status filter and SKU query args, which are props here.
 */
export const OrdersTable = memo<OrdersTableProps>((props) => {
  const {
    statusKey,
    status,
    excludeStatus,
    endDate,
    startDate,
    venue,
    accountManagers,
    invoiced,
    feeAdjustmentTypes,
    search,
  } = props;

  const [page, setPage] = useState<number>(1);
  const [sort, setSort] = useState<ServerSort | undefined>(undefined);
  const [pageSearch, setPageSearch] = useState(search);

  // A new search can have fewer pages than the one being viewed. Resetting
  // during render (not in an effect) avoids fetching the stale page first.
  if (search !== pageSearch) {
    setPageSearch(search);
    setPage(1);
  }

  const editOrderModalRef = useRef<EditOrderModalRef>(null);

  const washHubId = washHub.selectors.useSelectedWashHubId();
  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();

  const queryKey = [
    `${washHubId}-fetch-orders`,
    page,
    statusKey,
    endDate,
    startDate,
    venue?.toString(),
    accountManagers?.toString(),
    sort?.field,
    sort?.dir,
    invoiced,
    feeAdjustmentTypes?.toString(),
    search,
  ];

  const queryFn = ({ signal }: QueryFunctionContext) =>
    fetchOrders(
      {
        page,
        limit: 10,
        washHub: washHubId,
        endDate,
        startDate,
        ...(status !== undefined ? { status } : {}),
        ...(excludeStatus ? { excludeStatus } : {}),
        venue,
        ...(accountManagers && accountManagers.length
          ? { accountManagers }
          : {}),
        sortByField: sort?.field,
        sort: sort?.dir,
        invoiced,
        ...(feeAdjustmentTypes && feeAdjustmentTypes.length
          ? { feeAdjustmentTypes }
          : {}),
        ...(search ? { search } : {}),
      },
      signal,
    );

  const options = {
    enabled: isWashHubIdValid,
    keepPreviousData: true,
  };

  const { data, isLoading, isFetching } = useQuery(queryKey, queryFn, options);

  useWashHubListener(() => {
    setPage(1);
  });

  // The All tab used to pass activeOnly=0 here; useSKU coerces both 0 and
  // undefined to false and neither is in its query key, so the calls were
  // identical and one shared call covers all four tabs.
  const skuData = useSKU('item');

  const skuItemsById = useMemo(() => {
    const index: SkuItemsById = {};

    skuData.data?.forEach((sku) => {
      index[sku._id] = sku.itemsInCase;
    });

    return index;
  }, [skuData.data]);

  const columns = useMemo(() => buildOrderColumns(skuItemsById), [
    skuItemsById,
  ]);

  const orders = data?.items || [];

  const pagination = {
    page,
    setPage,
    totalCount: data?.total || 0,
    totalPages: data?.totalPages || 0,
  };

  return (
    <>
      <DataTable<Order>
        minW="1550px"
        data={orders}
        columns={columns}
        isLoading={isLoading || isFetching}
        pagination={pagination}
        sort={sort}
        onSortChange={setSort}
        onRowClick={(order) => {
          if (!order) {
            return;
          }

          editOrderModalRef.current?.onOpen(order);
        }}
        renderEmpty={() => (
          <Center p={8}>
            <Text fontSize="20px">No Orders available</Text>
          </Center>
        )}
      />

      <EditOrderModal
        ref={editOrderModalRef}
        renderNextOrderButton={(currentOrder) => {
          const currentIndex = orders.findIndex(
            (o) => o._id === currentOrder._id,
          );

          if (currentIndex === orders.length - 1) {
            return null;
          }

          return (
            <OrderNavButton
              type="right"
              onClick={() => {
                const nextOrder = orders[currentIndex + 1];

                editOrderModalRef.current?.onOpen(nextOrder);
              }}
            />
          );
        }}
        renderPreviousOrderButton={(currentOrder) => {
          const currentIndex = orders.findIndex(
            (o) => o._id === currentOrder._id,
          );

          if (currentIndex === 0) {
            return null;
          }

          return (
            <OrderNavButton
              type="left"
              onClick={() => {
                const previousOrder = orders[currentIndex - 1];

                editOrderModalRef.current?.onOpen(previousOrder);
              }}
            />
          );
        }}
      />
    </>
  );
});
